import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Music, X, Loader2, CheckCircle, Play, Pause, Download, Eye, Plus, ImagePlus } from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, RegenerateButton, useReconfigure,
} from './ReconfigureControls';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import { setSourcePickerHandler } from '../../utils/sourcePickerBridge';
import SendToTimelineButton from '../SendToTimelineButton';
import DirectorsTrigger from '../studio/DirectorsTrigger';
import PromptField from '../studio/PromptField';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, textNeed } from '../../utils/nodeRequirements';

const PRESET_PROMPTS = [
  { id: 'pop', label: 'Indie Pop', prompt: '[Verse 1]\nWalking through the neon glow,\ncity lights reflect below,\nevery shadow tells a story,\nevery corner, fading glory.\n\n[Chorus]\nWe are the echoes in the night,\nburning brighter than the light.\n\nGenre: Dreamy indie pop. Mood: Nostalgic and uplifting. Tempo: 110 BPM.' },
  { id: 'lofi', label: 'Lo-Fi', prompt: '[0:00 - 0:15] Intro: Soft lo-fi beat with muffled vinyl crackle and distant rain sounds.\n[0:15 - 0:45] Verse: Warm Fender Rhodes piano melody with gentle bass at 85 BPM.\n[0:45 - 1:15] Chorus: Full arrangement with lush pads and subtle strings.\n[1:15 - 1:45] Outro: Fade out with piano melody alone over vinyl crackle.\n\nGenre: Lo-fi chill hop. Mood: Cozy and reflective. Instrumental only.' },
  { id: 'chiptune', label: '8-Bit', prompt: 'A bright chiptune melody in C Major, retro 8-bit video game style at 140 BPM. Instrumental only, no vocals.' },
  { id: 'cinematic', label: 'Cinematic', prompt: 'Epic cinematic orchestral piece with brass swells, thunderous timpani, soaring string melodies. Genre: Film score. Mood: Heroic and triumphant. Duration: 2 minutes.' },
];

const Lyria3ProNode = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  const [prompt, setPrompt] = useState(data.prompt || '');
  const [images, setImages] = useState(data.images || []);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);
  const openSourcePicker = useWorkflowStore((state) => state.openSourcePicker);

  const { processNode, status: execStatus, error: execError, reset: resetExec, insufficientCredits } = useNodeExecution();
  const isProcessing = execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';
  const { openAudioPreview } = useMediaPreview();

  // El prompt tiene que vivir en node.data, no sólo en el estado de React:
  // si no, el servidor nunca lo recibe (validate_workflow lo marcaba como
  // faltante) y se perdía al recargar el proyecto.
  useEffect(() => {
    updateNodeData(id, { prompt });
  }, [id, prompt, updateNodeData]);

  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => setChronometer((prev) => prev + 1), 1000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePresetClick = (preset) => {
    setSelectedPreset(preset.id);
    setPrompt(preset.prompt);
  };

  const handleAddImage = useCallback(() => {
    if (images.length >= 5) return;
    setSourcePickerHandler((result) => {
      if (result.file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImages(prev => {
            const updated = [...prev, { file: result.file, preview: e.target.result, name: result.file.name }];
            updateNodeData(id, { images: updated });
            return updated;
          });
        };
        reader.readAsDataURL(result.file);
      } else if (result.url) {
        setImages(prev => {
          const updated = [...prev, { file: null, preview: result.url, name: result.fileName, fromGallery: true }];
          updateNodeData(id, { images: updated });
          return updated;
        });
      }
    });
    openSourcePicker(id, ['image'], 'image/jpeg,image/jpg,image/png,image/webp');
  }, [id, images.length, openSourcePicker, updateNodeData]);

  const removeImage = useCallback((index) => {
    setImages(prev => {
      const updated = prev.filter((_, i) => i !== index);
      updateNodeData(id, { images: updated });
      return updated;
    });
  }, [id, updateNodeData]);

  const togglePlay = useCallback(() => {
    const audio = document.getElementById(`lyria3pro-audio-${id}`);
    if (audio) {
      if (isPlaying) { audio.pause(); } else { audio.play(); }
      setIsPlaying(!isPlaying);
    }
  }, [id, isPlaying]);

  const fetchFileFromUrl = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new File([blob], filename, { type: blob.type || 'image/jpeg' });
    } catch (error) {
      console.error('Error fetching image:', error);
      return null;
    }
  };

  const handleProcess = useCallback(async () => {
    if (!prompt.trim()) return false;
    resetExec();

    // Collect image files for upload
    const files = [];
    const directParams = { prompt: prompt.trim(), image_count: images.length };
    for (let i = 0; i < images.length; i++) {
      let file = images[i].file;
      if (!file && images[i].preview) {
        if (images[i].preview.startsWith('http')) {
          directParams[`image_url_${i}`] = images[i].preview;
        } else {
          file = await fetchFileFromUrl(images[i].preview, images[i].name || `image_${i}.jpg`);
        }
      }
      if (file) files.push({ key: `image_url_${i}`, file });
    }

    const result = await processNode({
      resultType: 'lyria3_pro',
      files,
      params: directParams,
    });

    if (result?.outputUrl) {
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: result.outputUrl,
        assetId: result.assetId ?? null,
        outputType: 'audio',
        processedAt: new Date().toISOString(),
      });
      addAsset({
        id: `asset_${Date.now()}`,
        type: 'audio',
        tool: 'lyria3pro',
        filename: `lyria3pro_${prompt.slice(0, 20)}`,
        url: result.outputUrl,
        createdAt: new Date().toISOString(),
      });
      return true;
    }
    return false;
  }, [id, prompt, images, updateNodeData, addAsset, processNode, resetExec]);

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);
  const handleRegenerate = useCallback(() => {
    setReconfigure(false);
    updateNodeData(id, { hasOutput: false, outputUrl: null });
  }, [id, updateNodeData, setReconfigure]);
  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([textNeed(prompt, 'describir la música que querés')]);

  return (
    <div className={`node-container lyria3pro-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`} data-testid="lyria3pro-node">
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />
      {reconfigure && (
        <ReconfigureNotice
          previewUrl={data.thumbnailUrl || null}
          onCancel={() => setReconfigure(false)}
          testId="lyria3pro-reconfigure-notice"
        />
      )}

      <div className="node-header lyria3pro-header">
        <div className="node-header-left">
          <Music size={16} />
          <span>Lyria 3 Full Song</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)} data-testid="delete-lyria3pro">
          <X size={14} />
        </button>
      </div>

      <div className="node-content lyria3pro-content">
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">{formatTime(chronometer)}</div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>
                {execStatus === 'uploading' ? 'Subiendo archivos...' :
                 execStatus === 'queued' ? 'En cola...' :
                 'Generando canción...'}
              </span>
            </div>
            <p className="node-processing-hint">MP3 48kHz estéreo, hasta 3 min</p>
          </div>
        ) : !showForm ? (
          <div className="node-result lyria3pro-result">
            <audio id={`lyria3pro-audio-${id}`} src={data.outputUrl} onEnded={() => setIsPlaying(false)} />
            <div className="lyria3pro-player">
              <button className="lyria3pro-play-btn" onClick={togglePlay} data-testid="lyria3pro-play-btn">
                {isPlaying ? <Pause size={24} /> : <Play size={24} />}
              </button>
              <div className="lyria3pro-waveform">
                {[...Array(16)].map((_, i) => (
                  <div key={`wave-${i}`} className={`lyria3pro-wave-bar ${isPlaying ? 'animate' : ''}`} style={{ animationDelay: `${i * 0.08}s` }} />
                ))}
              </div>
            </div>
            <div className="node-result-actions">
              <RegenerateButton onClick={handleRegenerate} testId="lyria3pro-regen-btn" />
              <ReconfigureButton onClick={() => setReconfigure(true)} testId="lyria3pro-reconfigure-btn" />
              <button className="node-mini-preview-btn" onClick={() => openAudioPreview(data.outputUrl, 'Lyria 3 Full Song')} data-testid="lyria3pro-preview-btn">
                <Eye size={12} /> Ver
              </button>
              <button className="node-mini-download-btn" onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `song_${Date.now()}.mp3`); }} data-testid="lyria3pro-download-btn">
                <Download size={12} /> MP3
              </button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="audio" label="lyria3pro" testIdPrefix="lyria3pro-timeline" />
            </div>
            <div className="node-result-badge">
              <CheckCircle size={12} />
              <span>Canción generada</span>
            </div>
          </div>
        ) : (
          <>
            {/* Preset buttons */}
            <div className="lyria3pro-presets">
              <label>Estilos</label>
              <div className="lyria3pro-preset-grid">
                {PRESET_PROMPTS.map((preset) => (
                  <button
                    key={preset.id}
                    className={`lyria3pro-preset-btn ${selectedPreset === preset.id ? 'active' : ''}`}
                    onClick={() => handlePresetClick(preset)}
                    data-testid={`lyria3pro-preset-${preset.id}`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Prompt */}
            <div className="lyria3pro-prompt-section">
              <div className="nano-prompt-header">
                <label>Prompt *</label>
                <DirectorsTrigger nodeId={id} nodeType="lyria3pro" currentPrompt={prompt} onInsert={setPrompt} />
              </div>
              <PromptField
                value={prompt}
                onChange={(text) => { setPrompt(text); setSelectedPreset(null); }}
                nodeId={id}
                nodeType="lyria3pro"
                label="Describe la canción"
                placeholder="Describe la canción: género, instrumentos, mood, tempo, letras con [Verse], [Chorus]..."
                className="lyria3pro-prompt-input"
                rows={4}
                testId="lyria3pro-prompt"
              />
            </div>

            {/* Optional images */}
            <div className="lyria3pro-images-section">
              <span className="lyria3pro-images-label">
                <ImagePlus size={11} />
                Imágenes inspiración ({images.length}/5) — opcional
              </span>
              <div className="lyria3pro-images-row">
                {images.map((img, index) => (
                  <div key={img.name || `img-${index}`} className="lyria3pro-img-thumb">
                    <img src={img.preview} alt={`Ref ${index + 1}`} />
                    <button className="lyria3pro-img-clear" onClick={() => removeImage(index)} data-testid={`lyria3pro-remove-img-${index}`}>
                      <X size={8} />
                    </button>
                  </div>
                ))}
                {images.length < 5 && (
                  <button className="lyria3pro-add-img-btn" onClick={handleAddImage} data-testid="lyria3pro-add-image">
                    <Plus size={12} />
                  </button>
                )}
              </div>
            </div>

            <p className="lyria3pro-info">MP3 48kHz estéreo, hasta ~3 min</p>

            <button
              className="node-process-btn lyria3pro-process-btn"
              onClick={handleProcess}
              disabled={!prompt.trim()}
              title={requirementsTitle(runMissing)}
              data-testid="lyria3pro-generate-btn"
            >
              <Music size={14} />
              Generar Canción
            </button>
            <NodeRequirements missing={runMissing} />
          </>
        )}

        <NodeError error={execError} insufficientCredits={insufficientCredits} />
      </div>

      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

Lyria3ProNode.displayName = 'Lyria3ProNode';

export default Lyria3ProNode;
